import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Circle,
  Loader2,
  Eye,
  Ban,
  RotateCcw,
  Printer,
  Trash2
} from 'lucide-react';

const BlankBadges = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [badges, setBadges] = useState([]);
  const [summary, setSummary] = useState({ available: 0, activated: 0, voided: 0, total: 0 });
  const [batches, setBatches] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({}); // { category_id: { total, available, ... } }
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [downloading, setDownloading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskProgress, setTaskProgress] = useState(null);
  
  // Generate dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateTaskId, setGenerateTaskId] = useState(null);
  const [generateProgress, setGenerateProgress] = useState(null);
  const [generateForm, setGenerateForm] = useState({
    quantity: 50,
    code_prefix: 'REG',
    starting_number: 1,
    category_id: ''
  });
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  
  // Bulk Exhibitor Assignment dialog
  const [bulkExhibitorDialogOpen, setBulkExhibitorDialogOpen] = useState(false);
  const [bulkExhibitorForm, setBulkExhibitorForm] = useState({
    organization_name: '',
    category_id: '',
    badge_count: 1,
    scanned_codes: []
  });
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [scanningMode, setScanningMode] = useState(false);
  const [manualCodeInput, setManualCodeInput] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [eventId]);

  const fetchEvent = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/admin/events');
    }
  }, [eventId, navigate]);

  const fetchBadges = useCallback(async () => {
    try {
      const params = { page, limit: 50 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      
      const response = await api.get(`/events/${eventId}/blank-badges`, { params });
      setBadges(response.data.badges || []);
      setSummary(response.data.summary || { available: 0, activated: 0, voided: 0, total: 0 });
      setBatches(response.data.batches || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load blank badges');
    } finally {
      setLoading(false);
    }
  }, [eventId, page, statusFilter, selectedCategory]);

  const fetchCategoryCounts = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/blank-badges/summary-by-category`);
      setCategoryCounts(response.data.categories || {});
    } catch (error) {
      console.error('Failed to load category counts:', error);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    fetchCategories();
    fetchCategoryCounts();
  }, [fetchEvent, fetchCategories, fetchCategoryCounts]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  // Refetch category counts when badges change (after generate/delete)
  useEffect(() => {
    if (!loading) {
      fetchCategoryCounts();
    }
  }, [badges.length, fetchCategoryCounts, loading]);

  // Poll for task status
  const pollTaskStatus = async (taskId, type = 'download') => {
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/events/${eventId}/blank-badges/task/${taskId}`);
        const task = response.data;
        
        if (type === 'download') {
          setTaskProgress(task);
        } else {
          setGenerateProgress(task);
        }
        
        if (task.status === 'completed') {
          clearInterval(interval);
          
          if (type === 'download') {
            // Download the file
            const downloadResponse = await api.get(`/events/${eventId}/blank-badges/task/${taskId}/download`, {
              responseType: 'blob'
            });
            const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = task.filename || `blank_badges_${taskId}.pdf`;
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
            
            toast.success(`Downloaded ${task.total} blank badges`);
            setTaskId(null);
            setTaskProgress(null);
            setDownloading(false);
          } else {
            // Badge generation completed - NO auto-download
            toast.success(`Generated ${task.total} blank badges. Use "Download Unused" to download.`);
            setGenerateTaskId(null);
            setGenerateProgress(null);
            setGenerating(false);
            setGenerateDialogOpen(false);
            fetchBadges();
            
            setGenerateForm(prev => ({
              ...prev,
              starting_number: prev.starting_number + prev.quantity
            }));
          }
        } else if (task.status === 'failed') {
          clearInterval(interval);
          toast.error(task.error || 'Task failed');
          
          if (type === 'download') {
            setTaskId(null);
            setTaskProgress(null);
            setDownloading(false);
          } else {
            setGenerateTaskId(null);
            setGenerateProgress(null);
            setGenerating(false);
          }
        }
      } catch (error) {
        clearInterval(interval);
        console.error('Error polling task status:', error);
      }
    }, 2000);
    
    return interval;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      const payload = {
        quantity: generateForm.quantity,
        code_prefix: generateForm.code_prefix,
        starting_number: generateForm.starting_number
      };
      if (generateForm.category_id) {
        payload.category_id = generateForm.category_id;
      }
      
      const response = await api.post(`/events/${eventId}/blank-badges/generate`, payload);
      
      // Always background task
      setGenerateTaskId(response.data.task_id);
      setGenerateProgress({ status: 'pending', total: generateForm.quantity, processed: 0 });
      toast.info(`Generating ${generateForm.quantity} badges...`);
      pollTaskStatus(response.data.task_id, 'generate');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate badges');
      setGenerating(false);
    }
  };

  // Bulk Exhibitor Assignment handlers
  const handleAddBadgeCode = (code) => {
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) return;
    
    if (bulkExhibitorForm.scanned_codes.includes(cleanCode)) {
      toast.error('Badge already scanned');
      return;
    }
    
    if (bulkExhibitorForm.scanned_codes.length >= bulkExhibitorForm.badge_count) {
      toast.error(`Already scanned ${bulkExhibitorForm.badge_count} badges`);
      return;
    }
    
    setBulkExhibitorForm(prev => ({
      ...prev,
      scanned_codes: [...prev.scanned_codes, cleanCode]
    }));
    setManualCodeInput('');
    
    if (bulkExhibitorForm.scanned_codes.length + 1 >= bulkExhibitorForm.badge_count) {
      toast.success('All badges scanned!');
    }
  };

  const handleRemoveBadgeCode = (code) => {
    setBulkExhibitorForm(prev => ({
      ...prev,
      scanned_codes: prev.scanned_codes.filter(c => c !== code)
    }));
  };

  const handleBulkExhibitorAssign = async () => {
    if (!bulkExhibitorForm.organization_name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    if (!bulkExhibitorForm.category_id) {
      toast.error('Category is required');
      return;
    }
    if (bulkExhibitorForm.scanned_codes.length === 0) {
      toast.error('Please scan at least one badge');
      return;
    }
    
    setBulkAssigning(true);
    
    try {
      const response = await api.post(`/events/${eventId}/blank-badges/bulk-assign-exhibitor`, {
        organization_name: bulkExhibitorForm.organization_name.trim(),
        category_id: bulkExhibitorForm.category_id,
        registration_codes: bulkExhibitorForm.scanned_codes
      });
      
      const { success_count, failed_count, results } = response.data;
      
      if (success_count > 0) {
        toast.success(`Successfully assigned ${success_count} badges to ${bulkExhibitorForm.organization_name}`);
      }
      
      if (failed_count > 0) {
        results.failed.forEach(f => {
          toast.error(`${f.code}: ${f.error}`);
        });
      }
      
      // Reset form and close dialog
      setBulkExhibitorForm({
        organization_name: '',
        category_id: '',
        badge_count: 1,
        scanned_codes: []
      });
      setBulkExhibitorDialogOpen(false);
      fetchBadges();
      fetchCategoryCounts();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign badges');
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleVoid = async (code) => {
    if (!window.confirm(`Void badge ${code}? This badge will not be usable.`)) return;
    
    try {
      await api.post(`/events/${eventId}/blank-badges/${code}/void`);
      toast.success(`Badge ${code} voided`);
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void badge');
    }
  };

  const handleReset = async (code) => {
    if (!window.confirm(`Reset badge ${code} to available?`)) return;
    
    try {
      await api.post(`/events/${eventId}/blank-badges/${code}/reset`);
      toast.success(`Badge ${code} reset to available`);
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset badge');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete badge ${code}? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/events/${eventId}/blank-badges/${code}`);
      toast.success(`Badge ${code} deleted`);
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete badge');
    }
  };

  const handleDeleteAll = async (status) => {
    const statusLabel = status || 'all non-activated';
    const count = status === 'available' ? summary.available : 
                  status === 'voided' ? summary.voided : 
                  (summary.available + summary.voided);
    
    if (count === 0) {
      toast.error(`No ${statusLabel} badges to delete`);
      return;
    }
    
    if (!window.confirm(`Delete ${count} ${statusLabel} blank badges? This action cannot be undone.`)) return;
    
    try {
      const params = status ? `?status=${status}` : '';
      const response = await api.delete(`/events/${eventId}/blank-badges${params}`);
      toast.success(response.data.message || `Deleted ${count} badges`);
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete badges');
    }
  };

  const handleDownloadUnused = async () => {
    setDownloading(true);
    try {
      let url = `/events/${eventId}/blank-badges/download-unused`;
      if (selectedCategory !== 'all') {
        url += `?category_id=${selectedCategory}`;
      }
      
      const response = await api.get(url);
      
      // Always background task
      setTaskId(response.data.task_id);
      setTaskProgress({ status: 'pending', total: response.data.total, processed: 0 });
      toast.info(`Preparing ${response.data.total} badges for download...`);
      pollTaskStatus(response.data.task_id, 'download');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start download');
      setDownloading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/events/${eventId}/blank-badges`, { params: { limit: 10000 } });
      const allBadges = response.data.badges || [];
      
      // Create CSV with IST times
      const headers = ['Registration Code', 'Status', 'Category', 'Assigned To', 'Ticket ID', 'Activated At (IST)', 'Batch ID'];
      const rows = allBadges.map(b => [
        b.registration_code,
        b.status,
        categories.find(c => c.category_id === b.category_id)?.category_name || '-',
        b.attendee_name || '-',
        b.attendee_ticket_id || '-',
        b.activated_at ? new Date(b.activated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-',
        b.batch_id?.slice(0, 8) || '-'
      ]);
      
      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `blank_badges_report_${event?.campaign_id || 'event'}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleViewBadge = async (code) => {
    try {
      const response = await api.get(`/events/${eventId}/blank-badges/${code}`);
      setSelectedBadge(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load badge details');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge variant="outline" className="bg-gray-50"><Circle className="h-3 w-3 mr-1" /> Available</Badge>;
      case 'activated':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Activated</Badge>;
      case 'voided':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Voided</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/admin/events/${eventId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Event
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Blank Badges</h1>
              <p className="text-gray-500">{event?.event_name} - On-Spot Registration</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Report
            </Button>
            <Button variant="outline" onClick={() => setBulkExhibitorDialogOpen(true)} className="border-blue-400 text-blue-600 hover:bg-blue-50">
              <Plus className="h-4 w-4 mr-2" /> Bulk Exhibitor
            </Button>
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Generate Badges
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
              <div className="text-sm text-gray-500">Total Generated</div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-700">{summary.available}</div>
              <div className="text-sm text-green-600">Available</div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{summary.activated}</div>
              <div className="text-sm text-blue-600">Activated</div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-700">{summary.voided}</div>
              <div className="text-sm text-red-600">Voided</div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Delete Actions */}
        {(summary.available > 0 || summary.voided > 0) && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-orange-800">Bulk Delete Options</p>
                <p className="text-sm text-orange-600">Delete multiple blank badges at once (cannot delete activated badges)</p>
              </div>
              <div className="flex gap-2">
                {summary.available > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => handleDeleteAll('available')}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete {summary.available} Available
                  </Button>
                )}
                {summary.voided > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-300 text-red-700 hover:bg-red-100"
                    onClick={() => handleDeleteAll('voided')}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete {summary.voided} Voided
                  </Button>
                )}
                {(summary.available + summary.voided) > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteAll()}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete All Non-Activated
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Badges Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <CardTitle>Blank Badges</CardTitle>
              <div className="flex items-center gap-3">
                {/* Category Filter with Counts */}
                <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val); setPage(1); }}>
                  <SelectTrigger className="w-52" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>All Categories</span>
                        <Badge variant="secondary" className="ml-2">{summary.available} available</Badge>
                      </div>
                    </SelectItem>
                    {categories.map((cat) => {
                      const catCount = categoryCounts[cat.category_id];
                      return (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: cat.category_color || '#3B82F6' }}
                            />
                            <span>{cat.category_name}</span>
                            {catCount && (
                              <Badge variant="secondary" className="ml-1 text-xs">
                                {catCount.available || 0}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="activated">Activated</SelectItem>
                    <SelectItem value="voided">Voided</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Download Filtered Badges */}
                <Button 
                  variant="outline" 
                  onClick={handleDownloadUnused} 
                  disabled={summary.available === 0 || downloading}
                  data-testid="download-unused-btn"
                >
                  {downloading ? (
                    taskProgress ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {taskProgress.processed || 0}/{taskProgress.total || '?'}</>
                    ) : (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing...</>
                    )
                  ) : (
                    <><Download className="h-4 w-4 mr-2" /> Download {selectedCategory !== 'all' ? 'Filtered' : 'All'} Unused</>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Active Filter Indicator */}
            {selectedCategory !== 'all' && (
              <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
                <span>Filtering by category:</span>
                <Badge variant="outline" className="flex items-center gap-1">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: categories.find(c => c.category_id === selectedCategory)?.category_color || '#3B82F6' }}
                  />
                  {categories.find(c => c.category_id === selectedCategory)?.category_name || 'Unknown'}
                </Badge>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedCategory('all')}>
                  Clear
                </Button>
              </div>
            )}
            
            {badges.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Printer className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No blank badges {selectedCategory !== 'all' ? 'in this category' : 'generated yet'}</p>
                <p className="text-sm">Generate blank badges for on-spot registration</p>
                <Button className="mt-4" onClick={() => setGenerateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Generate Badges
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Ticket ID</TableHead>
                      <TableHead>Activated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {badges.map((badge) => {
                      const badgeCategory = categories.find(c => c.category_id === badge.category_id);
                      return (
                        <TableRow key={badge.blank_badge_id}>
                          <TableCell className="font-mono font-bold">{badge.registration_code}</TableCell>
                          <TableCell>
                            {badgeCategory ? (
                              <div className="flex items-center gap-1">
                                <div 
                                  className="w-2 h-2 rounded-full" 
                                  style={{ backgroundColor: badgeCategory.category_color || '#3B82F6' }}
                                />
                                <span className="text-sm">{badgeCategory.category_name}</span>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{getStatusBadge(badge.status)}</TableCell>
                          <TableCell>{badge.attendee_name || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">{badge.attendee_ticket_id || '-'}</TableCell>
                          <TableCell>
                            {badge.activated_at 
                              ? new Date(badge.activated_at).toLocaleString('en-IN', { 
                                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                                })
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {badge.status === 'activated' && (
                                <Button size="sm" variant="ghost" onClick={() => handleViewBadge(badge.registration_code)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              {badge.status === 'available' && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleVoid(badge.registration_code)}>
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(badge.registration_code)} title="Delete">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {badge.status === 'voided' && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => handleReset(badge.registration_code)}>
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(badge.registration_code)} title="Delete">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      Previous
                    </Button>
                    <span className="px-4 py-2 text-sm">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Batch History */}
        {batches.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Batch History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {batches.map((batch, idx) => (
                  <div key={batch._id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">Batch #{idx + 1}</span>
                      <span className="text-gray-500 ml-2">
                        {batch.created_at && new Date(batch.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </span>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-gray-600">{batch.count} badges</span>
                      <span className="text-green-600">{batch.available} available</span>
                      <span className="text-blue-600">{batch.activated} used</span>
                      {batch.voided > 0 && <span className="text-red-600">{batch.voided} voided</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Blank Badges</DialogTitle>
            <DialogDescription>
              Create blank badges with QR codes for on-spot registration. Select a category to use its badge template design.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div className="space-y-2">
              <Label>Badge Template (Category) *</Label>
              <Select
                value={generateForm.category_id}
                onValueChange={(value) => setGenerateForm({ ...generateForm, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category template" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.category_color || '#3B82F6' }}
                        />
                        {cat.category_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Blank badges will use this category&apos;s design template</p>
            </div>
            <div className="space-y-2">
              <Label>Quantity (1-500)</Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={generateForm.quantity}
                onChange={(e) => setGenerateForm({ ...generateForm, quantity: parseInt(e.target.value) || 1 })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code Prefix</Label>
                <Input
                  value={generateForm.code_prefix}
                  onChange={(e) => setGenerateForm({ ...generateForm, code_prefix: e.target.value.toUpperCase() })}
                  placeholder="REG"
                  maxLength={10}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Starting Number</Label>
                <Input
                  type="number"
                  min={1}
                  value={generateForm.starting_number}
                  onChange={(e) => setGenerateForm({ ...generateForm, starting_number: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Preview:</strong> {generateForm.code_prefix}-{String(generateForm.starting_number).padStart(3, '0')} to {generateForm.code_prefix}-{String(generateForm.starting_number + generateForm.quantity - 1).padStart(3, '0')}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
                Cancel
              </Button>
              <Button type="submit" disabled={generating || !generateForm.category_id}>
                {generating ? (
                  generateProgress ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {generateProgress.processed || 0}/{generateProgress.total || generateForm.quantity}</>
                  ) : (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting...</>
                  )
                ) : 'Generate Badges'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Badge Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Badge Details</DialogTitle>
          </DialogHeader>
          {selectedBadge && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Registration Code</Label>
                  <p className="font-mono font-bold">{selectedBadge.registration_code}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p>{getStatusBadge(selectedBadge.status)}</p>
                </div>
              </div>
              {selectedBadge.attendee && (
                <>
                  <hr />
                  <h4 className="font-medium">Attendee Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-gray-500">Name</Label>
                      <p>{selectedBadge.attendee.name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Ticket ID</Label>
                      <p className="font-mono">{selectedBadge.attendee.ticket_id}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Email</Label>
                      <p>{selectedBadge.attendee.email}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Phone</Label>
                      <p>{selectedBadge.attendee.phone_country_code} {selectedBadge.attendee.phone_number}</p>
                    </div>
                    {selectedBadge.attendee.gender && (
                      <div>
                        <Label className="text-gray-500">Gender</Label>
                        <p className="capitalize">{selectedBadge.attendee.gender}</p>
                      </div>
                    )}
                    {selectedBadge.attendee.tshirt_size && (
                      <div>
                        <Label className="text-gray-500">T-Shirt Size</Label>
                        <p>{selectedBadge.attendee.tshirt_size}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-500">Activated At</Label>
                    <p>{selectedBadge.activated_at && new Date(selectedBadge.activated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Exhibitor Assignment Dialog */}
      <Dialog open={bulkExhibitorDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setBulkExhibitorForm({
            organization_name: '',
            category_id: '',
            badge_count: 1,
            scanned_codes: []
          });
          setManualCodeInput('');
        }
        setBulkExhibitorDialogOpen(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Exhibitor Badge Assignment</DialogTitle>
            <DialogDescription>
              Assign multiple blank badges to an exhibitor organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Organization Name *</Label>
              <Input
                value={bulkExhibitorForm.organization_name}
                onChange={(e) => setBulkExhibitorForm(prev => ({ ...prev, organization_name: e.target.value }))}
                placeholder="e.g., Kaizen Infotech"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={bulkExhibitorForm.category_id}
                onValueChange={(v) => setBulkExhibitorForm(prev => ({ ...prev, category_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Number of Badges</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={bulkExhibitorForm.badge_count}
                onChange={(e) => setBulkExhibitorForm(prev => ({ ...prev, badge_count: parseInt(e.target.value) || 1 }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Enter Badge Codes ({bulkExhibitorForm.scanned_codes.length}/{bulkExhibitorForm.badge_count})</Label>
              <div className="flex gap-2">
                <Input
                  value={manualCodeInput}
                  onChange={(e) => setManualCodeInput(e.target.value.toUpperCase())}
                  placeholder="Enter registration code (e.g., REG-001)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBadgeCode(manualCodeInput);
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={() => handleAddBadgeCode(manualCodeInput)}
                  disabled={bulkExhibitorForm.scanned_codes.length >= bulkExhibitorForm.badge_count}
                >
                  Add
                </Button>
              </div>
            </div>
            
            {bulkExhibitorForm.scanned_codes.length > 0 && (
              <div className="space-y-2">
                <Label>Scanned Badges</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                  {bulkExhibitorForm.scanned_codes.map((code, idx) => (
                    <div key={code} className="flex items-center justify-between bg-gray-50 px-3 py-1 rounded">
                      <span>
                        <span className="text-gray-500 mr-2">{idx + 1}.</span>
                        <span className="font-mono">{code}</span>
                        <span className="text-gray-400 ml-2">→ {bulkExhibitorForm.organization_name || 'Org'} - {idx + 1}</span>
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveBadgeCode(code)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {bulkExhibitorForm.scanned_codes.length > 0 && bulkExhibitorForm.organization_name && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800">Preview:</p>
                <p className="text-blue-600">
                  {bulkExhibitorForm.scanned_codes.length} badge(s) will be assigned as:
                </p>
                <ul className="mt-1 text-blue-700">
                  {bulkExhibitorForm.scanned_codes.slice(0, 3).map((code, idx) => (
                    <li key={code}>• {bulkExhibitorForm.organization_name} - {idx + 1}</li>
                  ))}
                  {bulkExhibitorForm.scanned_codes.length > 3 && (
                    <li>• ... and {bulkExhibitorForm.scanned_codes.length - 3} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setBulkExhibitorDialogOpen(false)}
              disabled={bulkAssigning}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkExhibitorAssign}
              disabled={bulkAssigning || !bulkExhibitorForm.organization_name || !bulkExhibitorForm.category_id || bulkExhibitorForm.scanned_codes.length === 0}
            >
              {bulkAssigning ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Assigning...</>
              ) : (
                `Assign ${bulkExhibitorForm.scanned_codes.length} Badge(s)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlankBadges;
